/**
 * Medidor de contraseña. La política (NIST 800-63B-4) es solo de LARGO: mínimo
 * PASSWORD_MIN, máximo 128, sin reglas de composición. El medidor orienta hacia
 * frases largas y advierte lo obvio (repeticiones, secuencias, el propio correo o
 * nombre, contraseñas comunes); solo el largo bloquea el envío.
 */

import { PASSWORD_MIN } from "../../shared/contracts.js";

export const PASSWORD_MAX = 128;

export interface PasswordAssessment {
  /** 0 = no cumple el mínimo · 1 débil · 2 aceptable · 3 fuerte · 4 muy fuerte. */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Cumple la política del servidor (largo). */
  valid: boolean;
  hints: string[];
}

const COMMON = [
  "password", "contraseña", "contrasena", "123456", "qwerty", "admin", "sentinel", "chile", "seguro", "seguros",
  "bienvenido", "abc123", "iloveyou", "111111", "000000", "asdf", "clave",
];

const SEQUENCES = ["abcdefghijklmnopqrstuvwxyz", "0123456789", "qwertyuiopasdfghjklzxcvbnm"];

function hasSequence(pw: string, len = 4): boolean {
  const s = pw.toLowerCase();
  for (const seq of SEQUENCES) {
    for (let i = 0; i + len <= seq.length; i++) {
      const chunk = seq.slice(i, i + len);
      if (s.includes(chunk) || s.includes([...chunk].reverse().join(""))) return true;
    }
  }
  return false;
}

const fold = (v: string) => v.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

export function assessPassword(pw: string, personal: { email?: string; fullName?: string } = {}): PasswordAssessment {
  const hints: string[] = [];
  const length = [...pw].length;
  if (length < PASSWORD_MIN) {
    hints.push(`Usa al menos ${PASSWORD_MIN} caracteres (te faltan ${PASSWORD_MIN - length}).`);
    return { score: 0, label: length === 0 ? "Sin contraseña" : "Muy corta", valid: false, hints };
  }
  if (length > PASSWORD_MAX) {
    return { score: 0, label: "Muy larga", valid: false, hints: [`Máximo ${PASSWORD_MAX} caracteres.`] };
  }

  let points = length >= 20 ? 3 : length >= 16 ? 2 : 1;
  const classes = [/[a-zñ]/, /[A-ZÑ]/, /\d/, /[^\p{L}\d]/u].filter((r) => r.test(pw)).length;
  if (classes >= 3) points += 1;
  const words = pw.trim().split(/[\s\-_.]+/).filter((w) => w.length >= 3);
  if (words.length >= 4) points += 1;

  const unique = new Set(pw.toLowerCase()).size;
  if (unique <= Math.max(3, length / 4)) {
    points -= 2;
    hints.push("Tiene muchos caracteres repetidos.");
  }
  if (hasSequence(pw)) {
    points -= 1;
    hints.push("Evita secuencias como «1234» o «qwerty».");
  }
  const folded = fold(pw);
  if (COMMON.some((c) => folded.includes(c))) {
    points -= 1;
    hints.push("Contiene una palabra o clave muy común.");
  }
  const local = fold(personal.email?.split("@")[0] ?? "");
  const nameParts = fold(personal.fullName ?? "").split(/\s+/).filter((p) => p.length >= 3);
  if ((local.length >= 3 && folded.includes(local)) || nameParts.some((p) => folded.includes(p))) {
    points -= 2;
    hints.push("No uses tu nombre ni tu correo.");
  }
  if (points <= 1 && hints.length === 0) hints.push("Una frase de varias palabras es más segura y fácil de recordar.");

  const score = Math.min(4, Math.max(1, points)) as 1 | 2 | 3 | 4;
  const label = ["", "Débil", "Aceptable", "Fuerte", "Muy fuerte"][score] ?? "";
  return { score, label, valid: true, hints };
}
